<template>
  <div class="!pt-0">
    <div class="w-4/5 mx-auto flex flex-col max-h-screen pt-16 pb-2.5">
      <div class="my-2.5">
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              Subcontractor Variation Orders
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              Manage and track subcontractor variation orders
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="!fetchDashboardAction.firstLoad.value && fetchDashboardAction.is(States.LOADING)"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="flex flex-col items-center py-16">
          <AppSpinner class="h-8 w-8 mb-4" />

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            Loading subcontractors...
          </h3>
        </div>
      </div>

      <div
        v-else-if="!subcontractors.length"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="px-6 py-16 text-center">
          <div class="mx-auto h-12 w-12 text-gray-300 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            No subcontractors found
          </h3>

          <p class="text-sm text-gray-500 mb-6">
            No subcontractors have been assigned to packages for this project.
          </p>
        </div>
      </div>

      <div
        v-else
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto relative"
      >
        <!-- Table Header with Summary -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
          <div class="space-y-4">
            <!-- Header -->
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                <h3 class="text-lg font-semibold text-gray-900">
                  Subcontractors
                </h3>
              </div>
            </div>

            <!-- Summary Row -->
            <div class="bg-white rounded-lg border border-gray-200 p-4">
              <div class="flex justify-between gap-4">
                <div class="text-center md:text-left">
                  <p class="text-sm text-gray-500">
                    Subcontractors
                  </p>

                  <p class="text-lg font-semibold text-gray-900">
                    {{ subcontractors.length }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Subcontractors List -->
        <div class="divide-y divide-gray-200 relative">
          <SubcontractorVariationRow
            v-for="subcontractor in subcontractors"
            :key="subcontractor.id"
            :subcontractor="subcontractor"
            :project-supplier="subcontractor.projects?.[0]"
            :summary="summary"
            :class="{ 'opacity-50': fetchDashboardAction.is(States.LOADING) }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SubcontractorVariationRow from '@/components/Subcontract/SubcontractorVariationOrdersDashboard/SubcontractorVariationRow.vue';
import useBoqLockRedirect from '@/composables/useBoqLockRedirect';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import Supplier from '@/models/Supplier';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { useSuppliersStore } from '@/store/supplier';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

useBoqLockRedirect();
const packagesStore = usePackagesStore();
const suppliersStore = useSuppliersStore();
const projectsStore = useProjectsStore();
const project = computed(() => projectsStore.project);
const summary = ref<SubcontractVariationOrderDashboard['summary']>([]);
// API Actions
const fetchDashboardAction = useStoreApiAction(packagesStore.fetchVariationOrderDashboard);

watch(() => props.projectId, (newValue) => {
  if(newValue) {
    fetchDashboardAction.request(newValue).then((data) => {
      summary.value = data.summary;
    });
  }
}, { immediate: true });

// Computed
const packages = computed(() => {
  return packagesStore.models
    .where('project_id', Number(props.projectId))
    .with('subcontractor')
    .with('variation_orders')
    .get();
});

const subcontractors = computed(() => {
  return suppliersStore.models
    .whereHas('packages', (query) => {
      query.where('project_id', Number(props.projectId));
    })
    .with('projects', (query) => {
      query.where('project_id', Number(props.projectId))
        .with('supplier');
    })
    .with('packages', (query) => {
      query.where('project_id', Number(props.projectId))
        .with('variation_orders');
    })
    .get();
});

const totalPackages = computed(() => {
  return packages.value.length;
});

const variationSummary = computed(() => {
  const summary = {
    raised: 0,
    registered: 0,
    submitted: 0,
    provisioned: 0,
    approved: 0,
    total: 0,
  };

  packages.value.forEach((pkg) => {
    const variationOrders = pkg.variation_orders || [];

    if(variationOrders.length > 0) {
      summary.total++;

      variationOrders.forEach((vo) => {
        if(vo.status === VariationOrderStatus.raised) {
          summary.raised++;
        } else if(vo.status === VariationOrderStatus.registered) {
          summary.registered++;
        } else if(vo.status === VariationOrderStatus.submitted) {
          summary.submitted++;
        } else if(vo.status === VariationOrderStatus.provisioned) {
          summary.provisioned++;
        } else if(vo.status === VariationOrderStatus.approved) {
          summary.approved++;
        }
      });
    }
  });

  return summary;
});
</script>

<style scoped></style>
